"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import {X} from "lucide-react";

import clsxm from "@/lib/clsxm";
import {type ComponentPropsWithoutRef, type FC, type HTMLAttributes, type RefObject} from "react";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay: FC<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {ref?: RefObject<HTMLDivElement>}
> = ({ref, className, ...props}) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={clsxm(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent: FC<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {ref?: RefObject<HTMLDivElement>}
> = ({ref, className, children, ...props}) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={clsxm(
        "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-1/2 left-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg",
        className,
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
        <X className="size-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsxm("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsxm("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle: FC<ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {ref?: RefObject<HTMLDivElement>}> = ({
  ref,
  className,
  ...props
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsxm("text-lg leading-none font-semibold tracking-tight", className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription: FC<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {ref?: RefObject<HTMLDivElement>}
> = ({ref, className, ...props}) => (
  <DialogPrimitive.Description ref={ref} className={clsxm("text-muted-foreground text-sm", className)} {...props} />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
